import {Injectable} from "@angular/core";
import {Product} from "../product-details/interface/product";
import {MadeToOrderProfile} from "../product-details/interface/made-to-order-profile";
import {SizeProfile} from "../product-details/interface/size-profile";
import {FabricProfileItemList} from "../product-details/interface/fabric-profile-item-list";
import {VolumeDiscountProfileItem} from "../product-details/interface/volume-discount-profile-item";
import {SizeProfileOptionItem} from "../product-details/interface/size-profile-option-item";
import {ProductSizeProfileList} from "../product-details/interface/product-size-profile-list";

@Injectable({
	providedIn: 'root'
})
export class ProductInformationService {

	private _stockMessage: string = 'in stock!';
	private orderTypeInStock: boolean = true;
	private _consumedFabric: number = 1;
	private _makingCharges: number = 0;
	private _totalFinishPrice: number = 0;
	private _customSizePrice: number = 0;
	private _fabricPrice: number = 0;

	constructor() {
	}

	public calculateFinishProductPrice(product: Product, selectedFabricPrice: number, totalFinishPrice: number, customSizePrice: number): number {
		let calculatedPrice: number = 0;
		let selectedMadeToOrderFabricPrice: number = selectedFabricPrice;
		let consumedFabric: number = this.getConsumedFabric();
		let fabricPrice: number = selectedMadeToOrderFabricPrice * consumedFabric;

		calculatedPrice = product.price + fabricPrice + totalFinishPrice + customSizePrice;
		console.log(product.price, fabricPrice, totalFinishPrice, customSizePrice);
		this.setMakingCharges(product.price);
		this.setFabricPrice(fabricPrice);
		this.setTotalFinishPrice(totalFinishPrice);
		this.setCustomSizePrice(customSizePrice);
		return calculatedPrice;
	}

	public calculateFabricProductPrice(product: Product, selectedFabricPrice: number, totalFinishPrice: number, customSizePrice: number): number {
		let calculatedPrice: number = 0;
		let selectedMadeToOrderFabricPrice: number = selectedFabricPrice;
		let consumedFabric: number = this.getConsumedFabric();
		let madeToOrderFabricPrice: number = product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
		let fabricPrice: number = selectedMadeToOrderFabricPrice * consumedFabric;
		let makingCharges: number = product.price - madeToOrderFabricPrice;
		calculatedPrice = makingCharges + fabricPrice + totalFinishPrice + customSizePrice;
		console.log(makingCharges, fabricPrice, totalFinishPrice, customSizePrice);
		this.setMakingCharges(makingCharges);
		this.setFabricPrice(fabricPrice);
		this.setTotalFinishPrice(totalFinishPrice);
		this.setCustomSizePrice(customSizePrice);
		return calculatedPrice;
	}

	public calculateVDProductPrice(product: Product, selectedFabric: FabricProfileItemList | undefined, selectedVDProfile: VolumeDiscountProfileItem, quantity: number): number {
		let calculatedPrice: number = 0;
		let selectedMadeToOrderFabricPrice: number = 0;
		let consumedFabric: number = this.getConsumedFabric();
		let fabricPrice: number = 0;
		let makingCharges: number = 0;
		let selectedFabricQuantity: number = quantity;
		let volumeDiscountProfileItemList: VolumeDiscountProfileItem[] = [];
		if(product.productGroup === 'finished') {
			selectedMadeToOrderFabricPrice = selectedFabric ? selectedFabric.fabricPreview.price : product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			fabricPrice = selectedMadeToOrderFabricPrice * consumedFabric;
			selectedFabricQuantity = quantity * consumedFabric;
		}
		else {
			selectedMadeToOrderFabricPrice = selectedFabric ? selectedFabric.fabricPreview.price : product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			let madeToOrderFabricPrice: number = product.madeToOrderFabric ? product.madeToOrderFabric.price : 0;
			fabricPrice = selectedMadeToOrderFabricPrice * consumedFabric;
			makingCharges = product.price - madeToOrderFabricPrice;
		}

		if(selectedFabric && selectedFabric.fabricPreview.volumeDiscountProfile) {
			volumeDiscountProfileItemList = selectedFabric.fabricPreview.volumeDiscountProfile.volumeDiscountProfileItemList
				.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
		}
		else if(product.madeToOrderFabric && product.madeToOrderFabric.volumeDiscountProfile) {
			volumeDiscountProfileItemList = product.madeToOrderFabric.volumeDiscountProfile.volumeDiscountProfileItemList
				.sort((a, b) => b.minimumOrderQuantity - a.minimumOrderQuantity);
		}
		for (const item of volumeDiscountProfileItemList) {
			if (item.minimumOrderQuantity <= selectedFabricQuantity) {
				fabricPrice = this.calculateDiscountPrice(fabricPrice, item.discount);
				break;
			}
		}

		if(product.productGroup === 'finished')
			calculatedPrice = this.calculateDiscountPrice(product.price, selectedVDProfile.discount) + fabricPrice;
		else
			calculatedPrice = this.calculateDiscountPrice(makingCharges, selectedVDProfile.discount) + fabricPrice;
		return calculatedPrice;
	}

	public calculateDiscountPrice(price: number, discount: number): number {
		return price - (price * (discount / 100));
	}

	public calculateFabricProductQuantity(
		product: Product,
		selectedFabric: FabricProfileItemList | undefined,
		isCustomizationSelected: boolean
	): number {
		let calculatedQuantityInStock: number = 0;
		if ((!product.totalQuantity || selectedFabric) && product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
			const consumedFabric: number =  this.calculatedMTOConsumedFabric(product.madeToOrderProfile);
			this.setConsumedFabric(consumedFabric);
			const quantity: number = selectedFabric ? selectedFabric.fabricPreview.totalQuantity : product.madeToOrderFabric ? product.madeToOrderFabric.totalQuantity : product.totalQuantity;
			calculatedQuantityInStock = quantity > 0 ? Math.floor(quantity / consumedFabric): 0;
			this.setStockMessage('in stock.');
			this.setOrderTypeInStock(false);
		}
		else {
			calculatedQuantityInStock = product.totalQuantity;
			if(!isCustomizationSelected) {
				this.setStockMessage('in stock!');
				this.setOrderTypeInStock(true);
			}
		}
		this.setStockMessage(calculatedQuantityInStock > 0 ? this._stockMessage : 'Out of stock!');
		return calculatedQuantityInStock;
	}

	public calculateFinishProductQuantity(
		product: Product,
		selectedFabric: FabricProfileItemList | undefined,
		selectedSize: SizeProfileOptionItem,
		isCustomSizeSelected: boolean,
		isCustomizationSelected: boolean
	): number {
		let calculatedQuantityInStock: number = 0;
		let consumedFabricSize: number = -1;

		 if (selectedSize || isCustomSizeSelected) {
			 if(isCustomSizeSelected) {
				this.setStockMessage('in stock.');
				this.setOrderTypeInStock(false);
			 }
			 else if(selectedSize) {
				 consumedFabricSize = selectedSize.consumedFabric;
				 this.setConsumedFabric(consumedFabricSize);
				if(product.productSizeProfileList && product.productSizeProfileList.length > 0) {
					const size = product.productSizeProfileList.filter(size => !size.disabled).find(size => size.sizeProfileOption.label === selectedSize.label);
					if(size && !selectedFabric) {
						calculatedQuantityInStock = size.quantity;
						this.setStockMessage('in stock!');
						this.setOrderTypeInStock(true);
					}
				}
			 }

			if (!calculatedQuantityInStock && product.madeToOrderProfileEnabled && product.madeToOrderProfile) {
				 const consumedFabric: number =  (consumedFabricSize > 0)? consumedFabricSize : this.calculatedMTOConsumedFabric(product.madeToOrderProfile);
				 this.setConsumedFabric(consumedFabric);
				 const quantity: number = selectedFabric ? selectedFabric.fabricPreview.totalQuantity : product.madeToOrderFabric ? product.madeToOrderFabric.totalQuantity : product.totalQuantity;
				 calculatedQuantityInStock = quantity > 0 ? Math.floor(quantity / consumedFabric): 0;
				 this.setStockMessage('in stock.');
				 this.setOrderTypeInStock(false);
			 }
			 else {
				 calculatedQuantityInStock = calculatedQuantityInStock > 0 ? calculatedQuantityInStock : product.totalQuantity;
				 if(!isCustomizationSelected) {
					 this.setStockMessage('in stock!');
					 this.setOrderTypeInStock(true);
				 }
			 }
		}
		this.setStockMessage(calculatedQuantityInStock > 0 ? this._stockMessage : 'Out of stock!');
		return calculatedQuantityInStock;
	}

	public getConsumedFabric(): number {
		return this._consumedFabric;
	}

	public setConsumedFabric(consumedFabric: number): void {
		this._consumedFabric = consumedFabric;
	}

	public getMakingCharges(): number {
		return this._makingCharges;
	}

	public setMakingCharges(makingCharges: number): void {
		this._makingCharges = makingCharges;
	}

	public getFabricPrice(): number {
		return this._fabricPrice;
	}

	public setFabricPrice(fabricPrice: number): void {
		this._fabricPrice = fabricPrice;
	}

	public getTotalFinishPrice(): number {
		return this._totalFinishPrice;
	}

	public setTotalFinishPrice(totalFinishPrice: number): void {
		this._totalFinishPrice = totalFinishPrice;
	}

	public getCustomSizePrice(): number {
		return this._customSizePrice;
	}

	public setCustomSizePrice(customSizePrice: number): void {
		this._customSizePrice = customSizePrice;
	}

	public getStockMessage(): string {
		return this._stockMessage;
	}

	public setStockMessage(stockMessage: string): void {
		this._stockMessage = stockMessage;
	}

	public getOrderTypeInStock(): boolean {
		return this.orderTypeInStock;
	}

	public setOrderTypeInStock(orderTypeInStock: boolean): void {
		this.orderTypeInStock = orderTypeInStock;
	}

	public calculatedMTOConsumedFabric(madeToOrderProfile: MadeToOrderProfile): number {
		return madeToOrderProfile.consumedFabric > 0? madeToOrderProfile.consumedFabric :  1;
	}
	public calculatedSizeProfileConsumedFabric(sizeProfile: SizeProfile): number {
		return sizeProfile.sizeProfileOptionList.sort((a,b) => a.sortOrder - b.sortOrder)[0].consumedFabric;
	}
}
